import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  MbsPopupType,
  MbsSize,
  ModalService,
  SelectComponent
} from 'mbs-ui-kit';
import {
  CloudRestoreAddDestinationModalComponent,
  CloudRestoreAddDestinationParams
} from 'mbs-ui-shared@components/cloud-restore-add-destination-modal/cloud-restore-add-destination-modal.component';
import { first, noop } from 'rxjs';
import {
  EC2FullValue,
  EC2InstanceOption,
  EC2TargetInstanceStepValue,
  EC2TemporaryInstanceStepValue,
  ECInstanceStepValue
} from 'wizards/models/target-or-temporary-instance-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  EC2AimType,
  EC2BucketsType,
  EC2ConnectionType,
  EC2RegionType,
  EC2SecurityGroupType,
  EC2SubnetType,
  RestoreToCloudService
} from 'wizards/services/restore-to-cloud.service';

type TargetOrTemporaryInstanceControlKey = keyof Omit<ECInstanceStepValue, 'valid'>;

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AlertComponent, ButtonComponent, CardComponent, CardGroupComponent, FormsModule, I18NextPipe, SelectComponent],
  selector: 'mbs-ec2-instance-step',
  standalone: true,
  templateUrl: './ec2-instance-step.component.html'
})
export class EC2InstanceStepComponent implements OnInit {
  mainService = inject(RemoteManagementWizardsService);
  restoreToCloudService = inject(RestoreToCloudService);
  private modalService = inject(ModalService);
  private readonly initializedSelectKeys = new Set<TargetOrTemporaryInstanceControlKey>();
  private pendingRegionSyncAccountId: string | null = null;

  public readonly MbsSize = MbsSize;
  public readonly popupType = MbsPopupType;

  public readonly isTemporary = input<boolean>(false);

  public readonly connectionsLoading: WritableSignal<boolean> = signal(false);
  public readonly regionsLoading: WritableSignal<boolean> = signal(false);
  public readonly instanceTypesLoading: WritableSignal<boolean> = signal(false);
  public readonly tempDestinationsLoading: WritableSignal<boolean> = signal(false);
  public readonly subnetsLoading: WritableSignal<boolean> = signal(false);
  public readonly amisLoading: WritableSignal<boolean> = signal(false);
  public readonly securityGroupsLoading: WritableSignal<boolean> = signal(false);

  public readonly error: WritableSignal<string> = signal('');

  public readonly awsAccounts: WritableSignal<EC2InstanceOption[]> = signal([]);
  public readonly awsRegions: WritableSignal<EC2InstanceOption[]> = signal([]);
  public readonly tempDestinations: WritableSignal<EC2InstanceOption[]> = signal([]);
  public readonly instanceTypes: WritableSignal<string[]> = signal([]);
  public readonly subnets: WritableSignal<EC2InstanceOption[]> = signal([]);
  public readonly securityGroups: WritableSignal<EC2InstanceOption[]> = signal([]);
  public readonly amis: WritableSignal<EC2InstanceOption[]> = signal([]);

  public readonly value = model<ECInstanceStepValue>(EC2FullValue);
  public readonly ngModels = viewChildren(NgModel);

  public readonly awsAccount: WritableSignal<string> = signal(EC2FullValue.awsAccount);
  public readonly awsRegion: WritableSignal<string> = signal(EC2FullValue.awsRegion);
  public readonly tempDestination: WritableSignal<string> = signal(EC2FullValue.tempDestination);
  public readonly instanceType: WritableSignal<string> = signal(EC2FullValue.instanceType);
  public readonly subnet: WritableSignal<string> = signal(EC2FullValue.subnet);
  public readonly securityGroup: WritableSignal<string> = signal(EC2FullValue.securityGroup);
  public readonly ami: WritableSignal<string> = signal(EC2FullValue.ami);

  public readonly idPrefix = computed(() => (this.isTemporary() ? 'temporary-instance' : 'target-instance'));
  public readonly loading: Signal<boolean> = computed(
    () =>
      this.connectionsLoading() ||
      this.regionsLoading() ||
      this.instanceTypesLoading() ||
      (!this.isTemporary() && this.tempDestinationsLoading()) ||
      this.subnetsLoading() ||
      (this.isTemporary() && this.amisLoading()) ||
      this.securityGroupsLoading()
  );

  private createdDestinationsMap: { [key: string]: EC2InstanceOption[] } = {};
  private accountsMap: { [key: string]: string } = {};
  private regionsMap: { [key: string]: string } = {};

  constructor() {
    this.initBaseDataAndSubscribes();
  }

  /**
   * Requests are started here and not in the constructor:
   * cached responses are emitted synchronously, and a write to the `value` model
   * made before Angular applies the initial binding would be lost
   * */
  public ngOnInit(): void {
    this.getConnections();
    this.getInstanceTypes();
  }

  public isSelectDisabled(items: ReadonlyArray<string | EC2InstanceOption>): boolean {
    return !items.length || this.loading();
  }

  public valueChangeHandler(key: TargetOrTemporaryInstanceControlKey, value: string | null | undefined): void {
    const currentValue = this[key]();
    const nextValue = value ?? '';

    if (key === 'awsAccount' && currentValue !== nextValue) this.pendingRegionSyncAccountId = nextValue || null;
    if (key === 'awsRegion') this.pendingRegionSyncAccountId = null;

    this.initializedSelectKeys.add(key);
    this[key].set(nextValue);

    this.value.update((currentValue) => {
      const newValue = {
        ...(currentValue || this.getDefaultValue()),
        ...this.getDependentValues(key, nextValue),
        [key]: nextValue
      };

      return { ...newValue, valid: this.isStepValid(newValue) };
    });
  }

  public addDestination(): void {
    const userId = this.mainService.targetComputerData?.userId || this.mainService.userId;
    const account = this.awsAccount();
    const region = this.awsRegion();

    this.modalService
      .openCustom<string>(CloudRestoreAddDestinationModalComponent, {
        data: {
          UserId: userId,
          accountId: account,
          region,
          regionName: this.getRegionName(),
          tryAddDestination: (params: CloudRestoreAddDestinationParams) => this.restoreToCloudService.tryAddDestination(params)
        }
      })
      .then((bucketName: string) => {
        if (!bucketName) return;

        this.rememberCreatedDestination(account, region, bucketName);
        this.restoreToCloudService.invalidateEC2Buckets(userId, account, region);
        this.tempDestinations.set(this.withCreatedDestinations(this.tempDestinations(), account, region));
        this.valueChangeHandler('tempDestination', bucketName);
      })
      .catch(noop);
  }

  public forceValid(): void {
    this.ngModels().forEach((ngModel) => this.forceValidState(ngModel));
    this.updateValidState();
  }

  // Private section
  private initBaseDataAndSubscribes(): void {
    effect(() => {
      const account = this.awsAccount();

      untracked(() => this.getRegions(account));
    });

    effect(() => {
      const account = this.awsAccount();
      const region = this.awsRegion();
      const isTemporary = this.isTemporary();

      untracked(() => {
        this.getBuckets(account, region);
        this.getSubnets(account, region);

        if (isTemporary) this.getAims(account, region);
      });
    });

    effect(() => {
      const account = this.awsAccount();
      const region = this.awsRegion();
      const vpcId = this.getVpcId(this.subnet());

      untracked(() => this.getSecurityGroups(account, region, vpcId));
    });
    effect(() => {
      this.loading();
      this.isTemporary();

      untracked(() => this.updateValidState());
    });
  }

  private getConnections(): void {
    this.connectionsLoading.set(true);

    this.restoreToCloudService
      .getEC2Connections(this.mainService.targetComputerData?.userId || this.mainService.userId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2ConnectionType[]) => {
          const accounts: EC2ConnectionType[] = [];
          const destinationsMap = {};
          result.forEach((acc) => {
            this.accountsMap[acc.id] = acc.connectionId;
            if (!destinationsMap[acc.id]) {
              accounts.push(acc);
              destinationsMap[acc.id] = true;
            }
          });
          this.awsAccounts.set(accounts || []);
          this.setFirstOptionValueIfEmpty('awsAccount', accounts);
          this.connectionsLoading.set(false);
        },
        error: () => {
          this.awsAccounts.set([]);
          this.setFirstOptionValueIfEmpty('awsAccount', []);
          this.connectionsLoading.set(false);
        }
      });
  }

  private getRegions(accountId: string): void {
    if (!accountId) return;

    this.regionsLoading.set(true);

    this.restoreToCloudService
      .getEC2Regions(accountId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2RegionType[]) => {
          const regions = result?.map((el) => ({ ...el, id: el.value })) || [];

          regions.forEach((region) => (this.regionsMap[region.id] = region.region));
          this.awsRegions.set(regions);
          this.setRegion(regions, accountId);
          this.regionsLoading.set(false);
        },
        error: () => {
          this.awsRegions.set([]);
          this.setRegion([], accountId);
          this.regionsLoading.set(false);
        }
      });
  }

  private getInstanceTypes(): void {
    this.instanceTypesLoading.set(true);

    this.restoreToCloudService
      .getEC2InstanceTypes()
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: string[]) => {
          const instanceTypes = this.sortValues(result || []);

          this.instanceTypes.set(instanceTypes);
          this.syncValueWithAvailableValues('instanceType', instanceTypes[0], instanceTypes);
          this.instanceTypesLoading.set(false);
        },
        error: () => {
          this.instanceTypes.set([]);
          this.syncValueWithAvailableValues('instanceType', '');
          this.instanceTypesLoading.set(false);
        }
      });
  }

  private getBuckets(account: string, region: string): void {
    if (!account || !region) return;

    this.tempDestinationsLoading.set(true);

    this.restoreToCloudService
      .getEC2Buckets(this.mainService.targetComputerData?.userId || this.mainService.userId, account, region)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2BucketsType[]) => {
          const destinations = this.withCreatedDestinations(
            result?.map((el) => ({ ...el, name: el.value || el.name, id: el.value })) || [],
            account,
            region
          );

          this.tempDestinations.set(destinations);
          this.setFirstOptionValueIfEmpty('tempDestination', destinations);
          this.tempDestinationsLoading.set(false);
        },
        error: () => {
          this.tempDestinations.set([]);
          this.setFirstOptionValueIfEmpty('tempDestination', []);
          this.tempDestinationsLoading.set(false);
        }
      });
  }

  private getSubnets(account: string, region: string): void {
    if (!account || !region) return;

    this.subnetsLoading.set(true);

    this.restoreToCloudService
      .getEC2Subnets(account, region)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2SubnetType[]) => {
          const subnets = this.sortOptions(result?.map((el) => ({ ...el, id: el.subnetId })) || [], 'availabilityZone');

          this.subnets.set(subnets);
          this.setFirstOptionValueIfEmpty('subnet', subnets);
          this.subnetsLoading.set(false);
        },
        error: () => {
          this.subnets.set([]);
          this.setFirstOptionValueIfEmpty('subnet', []);
          this.subnetsLoading.set(false);
        }
      });
  }

  private getAims(account: string, region: string): void {
    if (!account || !region) return;

    this.amisLoading.set(true);

    this.restoreToCloudService
      .getEC2Aims(account, region)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2AimType[]) => {
          const amis = result?.map((el) => ({ ...el, id: el.value })) || [];

          this.amis.set(amis);
          this.setFirstOptionValueIfEmpty('ami', amis);
          this.amisLoading.set(false);
        },
        error: () => {
          this.amis.set([]);
          this.setFirstOptionValueIfEmpty('ami', []);
          this.amisLoading.set(false);
        }
      });
  }

  private getSecurityGroups(account: string, region: string, vpcId: string): void {
    if (!account || !region || !vpcId) return;

    this.securityGroupsLoading.set(true);

    this.restoreToCloudService
      .getEC2SecurityGroups(account, region, vpcId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: EC2SecurityGroupType[]) => {
          const securityGroups = this.sortOptions(result?.map((el) => ({ ...el, id: el.value })) || []);

          this.securityGroups.set(securityGroups);
          this.setFirstOptionValueIfEmpty('securityGroup', securityGroups);
          this.securityGroupsLoading.set(false);
        },
        error: () => {
          this.securityGroups.set([]);
          this.setFirstOptionValueIfEmpty('securityGroup', []);
          this.securityGroupsLoading.set(false);
        }
      });
  }

  private sortOptions<T extends EC2InstanceOption>(items: T[], key = 'name'): T[] {
    return [...items].sort(
      (first, second) => this.compareValues(first?.[key], second?.[key]) || this.compareValues(first?.name, second?.name)
    );
  }

  private sortValues(values: string[]): string[] {
    return [...values].sort((first, second) => this.compareValues(first, second));
  }

  private compareValues(first: unknown, second: unknown): number {
    return String(first ?? '').localeCompare(String(second ?? ''), undefined, { sensitivity: 'base' });
  }

  private setFirstOptionValueIfEmpty(
    key: TargetOrTemporaryInstanceControlKey,
    items: ReadonlyArray<EC2InstanceOption>,
    force = false
  ): void {
    this.syncValueWithAvailableValues(
      key,
      items[0]?.id,
      items.map((item) => item.id),
      force
    );
  }

  private syncValueWithAvailableValues(
    key: TargetOrTemporaryInstanceControlKey,
    value?: string,
    availableValues: ReadonlyArray<string | undefined> = value ? [value] : [],
    force = false
  ): void {
    const currentValue = this[key]();
    const isInitialized = this.initializedSelectKeys.has(key);

    this.initializedSelectKeys.add(key);

    if (force && value) {
      if (currentValue !== value) {
        this.valueChangeHandler(key, value);
        untracked(() => this.updateValidState());
      }

      return;
    }

    if (currentValue && !availableValues.includes(currentValue)) {
      this.valueChangeHandler(key, '');
      untracked(() => this.updateValidState());
      return;
    }

    if (currentValue || !value || isInitialized) return;

    this.valueChangeHandler(key, value);
    untracked(() => this.updateValidState());
  }

  private rememberCreatedDestination(account: string, region: string, bucketName: string): void {
    const key = this.getCreatedDestinationsKey(account, region);
    const created = this.createdDestinationsMap[key] || [];

    if (created.some((destination) => destination.id === bucketName)) return;

    this.createdDestinationsMap[key] = [...created, { id: bucketName, name: bucketName, value: bucketName }];
  }

  private withCreatedDestinations(destinations: EC2InstanceOption[], account: string, region: string): EC2InstanceOption[] {
    const created = this.createdDestinationsMap[this.getCreatedDestinationsKey(account, region)] || [];
    const missing = created.filter(
      (destination) => !destinations.some((item) => item.id === destination.id || item.name === destination.name)
    );

    return missing.length ? [...destinations, ...missing] : destinations;
  }

  private getCreatedDestinationsKey(account: string, region: string): string {
    return `${account}-${region}`;
  }

  private getRegionName(): string {
    const region = this.awsRegion();
    const regionOption = this.awsRegions().find((item) => item.id === region || item.value === region || item.name === region);

    return regionOption?.name || region;
  }

  private forceValidState(ngModel: NgModel): void {
    if (!ngModel) return;

    ngModel.control.markAsDirty();
    ngModel.control.markAsTouched();
    ngModel.control.updateValueAndValidity();
  }

  private updateValidState(): void {
    this.value.update((currentValue) => {
      const value = this.withSelectedValues(currentValue || this.getDefaultValue());

      return { ...value, valid: this.isStepValid(value) };
    });
  }

  private withSelectedValues(value: ECInstanceStepValue): ECInstanceStepValue {
    return this.getRequiredKeys().reduce((acc, key) => {
      const selected = this[key]();

      if (!selected || acc[key] === selected) return acc;

      return { ...acc, ...this.getDependentValues(key, selected), [key]: selected };
    }, value);
  }

  private getDependentValues(key: TargetOrTemporaryInstanceControlKey, value: string): Partial<ECInstanceStepValue> {
    if (key === 'subnet') {
      const subnetItem = this.getAvailabilityZoneZone(value);

      return { availabilityZone: subnetItem?.availabilityZone, subnetId: subnetItem?.subnetId };
    }

    if (key === 'awsAccount') return { connectionId: this.accountsMap[value] || '' };
    if (key === 'awsRegion') return { region: this.regionsMap[value] || '' };

    return {};
  }

  private getDefaultValue(): ECInstanceStepValue {
    return this.isTemporary() ? EC2TemporaryInstanceStepValue : EC2TargetInstanceStepValue;
  }

  private isStepValid(value: ECInstanceStepValue): boolean {
    return !this.loading() && this.getRequiredKeys().every((key) => !!value?.[key]);
  }

  private getRequiredKeys(): TargetOrTemporaryInstanceControlKey[] {
    const keys: TargetOrTemporaryInstanceControlKey[] = ['awsAccount', 'awsRegion', 'instanceType', 'subnet', 'securityGroup'];

    return this.isTemporary() ? [...keys, 'ami'] : [...keys, 'tempDestination'];
  }

  private getAvailabilityZoneZone(value: string): EC2InstanceOption {
    return this.subnets()?.find((r) => r.id === value);
  }

  private getVpcId(subnet: string): string {
    return this.getAvailabilityZoneZone(subnet)?.vpcId || '';
  }

  private setRegion(regions: EC2InstanceOption[], accountId: string): void {
    if (accountId !== this.awsAccount()) return;

    const selectedAcc = this.awsAccount();
    const current = this.awsRegion();
    const forceRegionSync = this.pendingRegionSyncAccountId === accountId;
    const foundAcc = this.awsAccounts()?.find((region) => region.id === selectedAcc);

    if (forceRegionSync) this.pendingRegionSyncAccountId = null;

    if (!foundAcc) return this.setFirstOptionValueIfEmpty('awsRegion', regions, forceRegionSync);

    const foundRegion = regions?.find((r) => r.id === foundAcc.region);

    if (!foundRegion) return this.setFirstOptionValueIfEmpty('awsRegion', regions, forceRegionSync);

    if (foundRegion?.id === current) return;

    this.syncValueWithAvailableValues(
      'awsRegion',
      foundRegion?.id,
      regions.map((region) => region.id),
      forceRegionSync
    );
  }
}
